import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null);
  const [resendBusy, setResendBusy] = useState(false);
  const [resendMsg, setResendMsg] = useState('');
  const [resendError, setResendError] = useState('');
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [resetError, setResetError] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const navigate = useNavigate();
  const { user, login, API_BASE_URL } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  useEffect(() => {
    document.title = 'Sign in — Sedra';
  }, []);

  useEffect(() => {
    if (user) {
      navigate('/customers');
    }
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setUnverifiedEmail(null);
    setResendMsg('');
    setResendError('');
    setIsSubmitting(true);
    try {
      await login(email, password);
      // Redirect will happen via useEffect when user is set
    } catch (err: any) {
      if (err?.code === 'unverified') {
        setUnverifiedEmail(email);
        setError("This account hasn't been verified yet, please verify your email before continuing.");
      } else {
        setError(err.message || 'Login failed');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetError('');
    setResetMessage('');
    setIsResetting(true);
    
    try {
      const response = await axios.post(`${API_BASE_URL}/users/forgot-password`, { email: resetEmail });
      setResetMessage(response.data.message);
      setResetEmail('');
    } catch (err: any) {
      setResetError(err.response?.data?.error || 'Failed to send reset request');
    } finally {
      setIsResetting(false);
    }
  };

  const openForgotPassword = () => {
    setForgotPasswordOpen(true);
    setResetEmail('');
    setResetMessage('');
    setResetError('');
  };

  const closeForgotPassword = () => {
    setForgotPasswordOpen(false);
    setResetEmail('');
    setResetMessage('');
    setResetError('');
  };

  const handleResendVerification = async () => {
    if (!unverifiedEmail) return;
    setResendBusy(true);
    setResendMsg('');
    setResendError('');
    try {
      await axios.post(`${API_BASE_URL}/auth/resend-verification`, { email: unverifiedEmail }, { withCredentials: true });
      setResendMsg('Verification email sent. Please check your inbox.');
    } catch (e: any) {
      setResendError(e?.response?.data?.error || 'Failed to resend verification email');
    } finally {
      setResendBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-md px-6 pt-16">
        <header className="text-center">
          <img src="/logo-color.svg" alt="" aria-hidden="true" className="mx-auto h-8 w-auto" />
          <h1 className="display-hero mt-6 text-3xl font-extrabold tracking-tight text-gray-900">Welcome back</h1>
          <p className="mt-2 text-sm text-gray-600">
            Don’t have an account? <a href="/register" className="text-sedra hover:underline">Create one</a>
          </p>
        </header>
        <main id="main" className="mt-8">
          <div className="rounded-2xl border border-gray-200 bg-white p-6 elev-1">
            <form onSubmit={handleSubmit} noValidate>
              <div className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    autoFocus
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    aria-invalid={!!error}
                    aria-describedby={error ? 'login-error' : undefined}
                    className="mt-1 block w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-gray-900 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2"
                  />
                </div>
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">Password</label>
                  <div className="relative">
                    <input
                      id="password"
                      name="password"
                      type={showPwd ? 'text' : 'password'}
                      required
                      autoComplete="current-password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      aria-invalid={!!error}
                      aria-describedby={error ? 'login-error' : undefined}
                      className="mt-1 block w-full rounded-xl border border-gray-300 bg-white px-3 py-2 pr-12 text-gray-900 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd(v => !v)}
                      aria-label={showPwd ? 'Hide password' : 'Show password'}
                      className="absolute inset-y-0 right-3 my-auto h-8 px-2 rounded-md text-gray-500 hover:text-gray-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sedra"
                    >
                      {showPwd ? 'Hide' : 'Show'}
                    </button>
                  </div>
                </div>

                {error && (
                  <div id="login-error" role="alert" className="rounded-2xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {error}
                  </div>
                )}

                {unverifiedEmail && (
                  <div className="rounded-2xl border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-900 space-y-2">
                    <div>
                      We can resend a verification email to <strong>{unverifiedEmail}</strong>.
                    </div>
                    <div className="flex gap-2 items-center">
                      <button
                        type="button"
                        onClick={handleResendVerification}
                        disabled={resendBusy}
                        className="rounded-lg bg-sedra px-3 py-1.5 text-white text-sm font-semibold disabled:opacity-60"
                      >
                        {resendBusy ? 'Sending…' : 'Resend verification email'}
                      </button>
                      <a
                        href={`/check-email?email=${encodeURIComponent(unverifiedEmail)}`}
                        className="text-sm text-sedra hover:underline ml-2"
                      >
                        Open “check your email” page
                      </a>
                      {resendMsg && <span className="text-green-700">{resendMsg}</span>}
                      {resendError && <span className="text-red-700">{resendError}</span>}
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  aria-busy={isSubmitting}
                  className="w-full rounded-xl bg-sedra px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-sedra-700 disabled:opacity-70 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra"
                >
                  {isSubmitting ? 'Signing in…' : 'Login'}
                </button>

                <a
                  href="/register"
                  className="block w-full text-center rounded-xl border border-sedra px-4 py-2.5 text-sm font-semibold text-sedra transition-colors hover:bg-sedra/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra"
                >
                  Create an account
                </a>
              </div>
            </form>
          </div>
          <div className="mt-3 text-center">
            <button type="button" onClick={openForgotPassword} className="text-sm font-medium text-sedra hover:underline">
              Forgot Password?
            </button>
          </div>
        </main>

        {/* Forgot Password Dialog */}
        <Dialog open={forgotPasswordOpen} onClose={closeForgotPassword} maxWidth="sm" fullWidth>
          <DialogTitle>Reset Password</DialogTitle>
          <DialogContent>
            <p className="text-sm text-gray-600 mb-2">
              Enter your email address and we'll send you a link to reset your password.
            </p>
            <form onSubmit={handleForgotPassword}>
              <TextField
                label="Email"
                type="email"
                value={resetEmail}
                onChange={e => setResetEmail(e.target.value)}
                fullWidth
                margin="normal"
                required
                disabled={isResetting}
              />

              {resetError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {resetError}
                </Alert>
              )}

              {resetMessage && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  {resetMessage}
                </Alert>
              )}

              <DialogActions sx={{ mt: 2, px: 0 }}>
                <Button onClick={closeForgotPassword} disabled={isResetting}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  variant="contained" 
                  disabled={!resetEmail || isResetting}
                >
                  {isResetting ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </DialogActions>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}; 